<template>
  <div>
    <vue-data-table
      :headers="headers"
      :data="suppliers"
      :items-per-page="10"
      :buttons="buttons"
      :filter-fields="filterFields"
      :filter-data="filter"
      filter-title="Filtrer les fournisseurs"
      :tableActions="tableActions"
      @filter="toFilter"
      @reset="resetFilter"
    >
      <!-- Full Name Column -->
      <template #cell-fullName="{ row }">
        <span class="fw-semibold">
          {{ row.fullName || '—' }}
        </span>
      </template>

      <!-- Society Column -->
      <template #cell-society="{ row }">
        <span v-if="row.society" class="text-primary">
          {{ row.society }}
        </span>
        <span v-else class="text-muted">—</span>
      </template>

      <!-- Phone Column -->
      <template #cell-phone="{ row }">
        <span v-if="row.phone" class="text-info">
          <i class="bi bi-telephone-fill me-1"></i>
          {{ row.phone }}
        </span>
        <span v-else class="text-muted">—</span>
      </template>

      <!-- Email Column -->
      <template #cell-email="{ row }">
        <span v-if="row.email" class="text-muted">
          <i class="bi bi-envelope-fill me-1"></i>
          {{ row.email }}
        </span>
        <span v-else class="text-muted">—</span>
      </template>

      <!-- Address Column -->
      <template #cell-address="{ row }">
        <span v-if="row.address" class="text-muted" :title="row.address">
          <i class="bi bi-geo-alt-fill me-1"></i>
          {{ row.address.length > 30 ? row.address.substring(0, 30) + '...' : row.address }}
        </span>
        <span v-else class="text-muted">—</span>
      </template>

      <!-- City Column -->
      <template #cell-city="{ row }">
        <span v-if="row.city" class="badge bg-light text-dark">
          {{ row.city }}
        </span>
        <span v-else class="text-muted">—</span>
      </template>

      <!-- ICE Column -->
      <template #cell-ice="{ row }">
        <span v-if="row.ice" class="text-muted">
          {{ row.ice }}
        </span>
        <span v-else class="text-muted">—</span>
      </template>

      <!-- RIB Column -->
      <template #cell-rib="{ row }">
        <span v-if="row.rib" class="text-muted">
          {{ row.rib }}
        </span>
        <span v-else class="text-muted">—</span>
      </template>

      <!-- Type Supplier Column -->
      <template #cell-typeSupplier="{ row }">
        <span v-if="row.typeSupplier" class="badge" :class="getTypeBadgeClass(row.typeSupplier)">
          {{ row.typeSupplier }}
        </span>
        <span v-else class="text-muted">—</span>
      </template>

      <!-- Remark Column -->
      <template #cell-remark="{ row }">
        <span v-if="row.remark" class="text-muted" :title="row.remark">
          {{ row.remark.length > 40 ? row.remark.substring(0, 40) + '...' : row.remark }}
        </span>
        <span v-else class="text-muted">—</span>
      </template>
    </vue-data-table>
  </div>
</template>

<script>
import { mapGetters } from "vuex";

const defaultFilter = () => ({
  city: "",
  typeSupplier: "",
});

export default {
  data() {
    return {
      filter: defaultFilter(),
      tableActions: [
        {
          title: "Actualiser",
          icon: "bi bi-arrow-clockwise",
          class: "btn btn-secondary",
          action: () => this.refresh(),
        },
        {
          title: "Créer un nouveau fournisseur",
          icon: "bi bi-plus-square",
          class: "btn btn-primary",
          action: () => this.$router.push({ name: "suppliers-create" }),
        },
      ],
      headers: [
        { field: "reference", label: "Référence", class: "text-left fw-bold" },
        { field: "fullName", label: "Nom Complet", class: "text-left" },
        { field: "society", label: "Nom Commercial", class: "text-left" },
        { field: "phone", label: "Téléphone", class: "text-left" },
        { field: "email", label: "E-mail", class: "text-left" },
        { field: "city", label: "Ville", class: "text-center" },
        { field: "address", label: "Adresse", class: "text-left" },
        { field: "ice", label: "I.C.E", class: "text-left" },
        { field: "rib", label: "R.I.B", class: "text-left" },
        { field: "typeSupplier", label: "Type", class: "text-center" },
        { field: "remark", label: "Remarque", class: "text-left" },
      ],
      buttons: [
        {
          label: "Modifier",
          icon: "bi bi-pencil",
          class: "btn btn-warning",
          action: (row) => {
            this.$router.push({
              name: "suppliers-edit",
              params: { reference: row.reference },
            });
          },
        },
        {
          label: "Supprimer",
          icon: "bi bi-trash",
          class: "btn btn-danger",
          action: (row) => this.remove(row),
        }
      ],
    };
  },
  computed: {
    ...mapGetters("supplier", {
      suppliers: "getAll",
    }),
    filterFields() {
      // Get unique cities and types from suppliers
      const cities = [...new Set(this.suppliers.map(s => s.city).filter(Boolean))].sort();
      const types = [...new Set(this.suppliers.map(s => s.typeSupplier).filter(Boolean))].sort();

      return [
        {
          field: "city",
          label: "Ville",
          type: "select",
          options: cities.map(city => ({
            value: city,
            label: city
          })),
          required: false
        },
        {
          field: "typeSupplier",
          label: "Type de fournisseur",
          type: "select",
          options: types.map(type => ({
            value: type,
            label: type
          })),
          required: false
        }
      ];
    }
  },
  mounted() {
    this.$store.dispatch("supplier/getAll");
  },
  methods: {
    async refresh() {
      await this.$store.dispatch("supplier/getAll");
    },
    async toFilter(data) {
      // Filter logic can be implemented here if needed
      // For now, the datatable handles client-side filtering
    },
    resetFilter() {
      this.filter = defaultFilter();
    },
    async remove(supplier) {
      await this.$confirm({
        title: `Supprimer le fournisseur ?`,
        message: `Voulez-vous vraiment supprimer le fournisseur ${supplier.society || supplier.fullName} ?`,
        button: {
          no: "Non",
          yes: "Oui",
        },
        callback: async (confirm) => {
          if (confirm) {
            await this.$store.dispatch('supplier/destroy', supplier.reference);
            await this.refresh();
          }
        },
      });
    },
    getTypeBadgeClass(type) {
      const classes = {
        'Fournisseur National': 'bg-primary',
        'Fournisseur International': 'bg-info',
        'Transitaire': 'bg-warning',
        'Transport National': 'bg-success',
        'Transport International': 'bg-secondary'
      };
      return classes[type] || 'bg-secondary';
    }
  },
};
</script>
